import React, { useState, useEffect } from 'react';
import { Form } from 'react-bootstrap';
import FavoriteDropdown from './FavoriteDropdown';
import {
  getFurnitureTypes,
  getFurnitureModelsByTypeId,
  getModelColorsByModelId,
  getModelSizesByModelId,
  getProductPrice,
  getProductVariantsByTypeId,
  getAvailableModelColors,
  getAvailableModelSizes
} from '../../api';

const ProductSelector = ({
  initialTypeId = '',
  initialModelId = '',
  initialColorId = '',
  initialSizeId = '',
  onSelectionChange,
  onFieldChange,
  disabled = false,
  furnitureTypes: furnitureTypesProp = null,
}) => {
  // State for data
  const [furnitureTypes, setFurnitureTypes] = useState(furnitureTypesProp || []);
  const [models, setModels] = useState([]);
  const [colors, setColors] = useState([]);
  const [sizes, setSizes] = useState([]);
  const [variants, setVariants] = useState([]);
  
  // State for selections
  const [selectedTypeId, setSelectedTypeId] = useState(initialTypeId);
  const [selectedModelId, setSelectedModelId] = useState(initialModelId);
  const [selectedColorId, setSelectedColorId] = useState(initialColorId);
  const [selectedSizeId, setSelectedSizeId] = useState(initialSizeId);
  
  // Loading state
  const [loading, setLoading] = useState(false);
  
  // Flag to prevent auto-selection
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Load furniture types on component mount
  useEffect(() => {
    if (furnitureTypesProp) setFurnitureTypes(furnitureTypesProp);
  }, [furnitureTypesProp]);

  // Load furniture types on component mount
  useEffect(() => {
    if (furnitureTypesProp) return;
    const loadFurnitureTypes = async () => {
      try {
        setLoading(true);
        const data = await getFurnitureTypes(false);
        setFurnitureTypes(data);
        
        // Auto-select if only one type and no initial value
        if (data.length === 1 && !initialTypeId && isInitialLoad) {
          setSelectedTypeId(data[0].id);
        }
      } catch (error) {
        console.error('Error loading furniture types:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadFurnitureTypes();
  }, [initialTypeId, isInitialLoad]);

  // Load models when type changes
  useEffect(() => {
    if (!selectedTypeId) {
      setModels([]);
      return;
    }
    
    const loadModels = async () => {
      try {
        setLoading(true);
        const [modelsData, variantsData] = await Promise.all([
          getFurnitureModelsByTypeId(selectedTypeId, false),
          getProductVariantsByTypeId(selectedTypeId, false)
        ]);
        
        setModels(modelsData);
        setVariants(variantsData);
        
        // Auto-select if only one model and no initial value
        if (modelsData.length === 1 && !initialModelId && isInitialLoad) {
          setSelectedModelId(modelsData[0].id);
        } else if (initialModelId && modelsData.some(m => m.id === initialModelId) && isInitialLoad) {
          setSelectedModelId(initialModelId);
        }
      } catch (error) {
        console.error('Error loading models:', error);
      } finally {
        setLoading(false);
        if (isInitialLoad) setIsInitialLoad(false);
      }
    };
    
    loadModels();
  }, [selectedTypeId, initialModelId, isInitialLoad]);

  // Load colors and sizes when model changes
  useEffect(() => {
    if (!selectedModelId) {
      setColors([]);
      setSizes([]);
      return;
    }
    
    const loadColorsAndSizes = async () => {
      try {
        setLoading(true);
        const [colorsData, sizesData] = await Promise.all([
          getModelColorsByModelId(selectedModelId, false),
          getModelSizesByModelId(selectedModelId, false)
        ]);
        
        setColors(colorsData);
        setSizes(sizesData);
        
        // Auto-select if only one option
        if (colorsData.length === 1 && !selectedColorId) {
          setSelectedColorId(colorsData[0].id);
        }
        
        if (sizesData.length === 1 && !selectedSizeId) {
          setSelectedSizeId(sizesData[0].id);
        }
      } catch (error) {
        console.error('Error loading colors and sizes:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadColorsAndSizes();
  }, [selectedModelId]);

  // Update available options when color changes
  const handleColorChange = async (colorId) => {
    setSelectedColorId(colorId);
    setSelectedSizeId('');
    
    if (onFieldChange) {
      onFieldChange('colorId', colorId);
      return;
    }
    
    if (selectedTypeId && selectedModelId) {
      try {
        setLoading(true);
        const availableSizes = colorId 
          ? await getAvailableModelSizes(selectedTypeId, selectedModelId, colorId, false)
          : await getModelSizesByModelId(selectedModelId, false);
        setSizes(availableSizes);
        
        notifySelectionChange(selectedTypeId, selectedModelId, colorId, '');
      } catch (error) {
        console.error('Error loading available sizes:', error);
      } finally {
        setLoading(false);
      }
    } else {
      notifySelectionChange(selectedTypeId, selectedModelId, colorId, '');
    }
  };

  // Update available options when size changes
  const handleSizeChange = async (sizeId) => {
    setSelectedSizeId(sizeId);
    
    if (onFieldChange) {
      onFieldChange('sizeId', sizeId);
      return;
    }
    
    if (selectedTypeId && selectedModelId) {
      try {
        setLoading(true);
        const availableColors = sizeId 
          ? await getAvailableModelColors(selectedTypeId, selectedModelId, sizeId, false)
          : await getModelColorsByModelId(selectedModelId, false);
        setColors(availableColors);
        
        // Keep color if still available, otherwise clear
        const newColorId = selectedColorId && availableColors.some(c => c.id === Number(selectedColorId)) 
          ? selectedColorId 
          : '';
        setSelectedColorId(newColorId);
        
        notifySelectionChange(selectedTypeId, selectedModelId, newColorId, sizeId);
      } catch (error) {
        console.error('Error loading available colors:', error);
      } finally {
        setLoading(false);
      }
    } else {
      notifySelectionChange(selectedTypeId, selectedModelId, selectedColorId, sizeId);
    }
  };

  // Handle type selection
  const handleTypeChange = (typeId) => {
    if (typeId !== selectedTypeId) {
      setSelectedTypeId(typeId);
      setSelectedModelId('');
      setSelectedColorId('');
      setSelectedSizeId('');
      
      if (onFieldChange) {
        onFieldChange('typeId', typeId);
      } else {
        notifySelectionChange(typeId, '', '', '');
      }
    }
  };

  // Handle model selection - CRITICAL FIX HERE
  const handleModelChange = (modelId) => {
    if (modelId !== selectedModelId) {
      setSelectedModelId(modelId);
      setSelectedColorId('');
      setSelectedSizeId('');
      
      if (onFieldChange) {
        onFieldChange('modelId', modelId);
      } else {
        notifySelectionChange(selectedTypeId, modelId, '', '');
      }
    }
  };

  // Notify parent component of selection changes
  const notifySelectionChange = async (typeId, modelId, colorId, sizeId) => {
    if (!onSelectionChange) return;
    
    const selection = {
      typeId: typeId || '',
      modelId: modelId || '',
      colorId: colorId || '',
      sizeId: sizeId || '',
      variants: variants || [],
      price: null
    };
    
    // Get price if all fields are selected
    if (typeId && modelId && colorId && sizeId) {
      try {
        const priceData = await getProductPrice({
          typeId,
          modelId,
          colorId,
          sizeId
        });
        selection.price = priceData.price;
        selection.productPriceId = priceData.id;
      } catch (error) {
        console.error('Error fetching price:', error);
      }
    }
    
    onSelectionChange(selection);
  };

  // Notify when selections change
  useEffect(() => {
    if (!isInitialLoad) {
      notifySelectionChange(selectedTypeId, selectedModelId, selectedColorId, selectedSizeId);
    }
  }, [selectedTypeId, selectedModelId, selectedColorId, selectedSizeId, isInitialLoad]);

  return (
    <div className="product-selector">
      {/* Tipo de producto */}
      <Form.Group className="mb-3">
        <Form.Label>Tipo de Producto</Form.Label>
        <FavoriteDropdown
          value={selectedTypeId}
          onChange={(e) => handleTypeChange(e.target.value)}
          options={furnitureTypes}
          placeholder="Seleccionar tipo"
          nameField="typeName"
          disabled={disabled || loading}
          className="styled-select"
          data-testid="product-type-selector"
        />
      </Form.Group>

      {/* Modelo */}
      {selectedTypeId && (
        <Form.Group className="mb-3">
          <Form.Label>Modelo</Form.Label>
          <FavoriteDropdown
            value={selectedModelId}
            onChange={(e) => handleModelChange(e.target.value)}
            options={models}
            placeholder="Seleccionar modelo"
            nameField="modelName"
            disabled={disabled || loading || !selectedTypeId}
            className="styled-select"
            data-testid="product-model-selector"
          />
        </Form.Group>
      )}

      {/* Color y Tamaño */}
      {selectedModelId && (
        <div className="row">
          {colors.length > 0 && (
            <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label>Color</Form.Label>
                <FavoriteDropdown
                  value={selectedColorId}
                  onChange={(e) => handleColorChange(e.target.value)}
                  options={colors}
                  placeholder="Seleccionar color"
                  nameField="colorName"
                  disabled={disabled || loading || !selectedModelId}
                  className="styled-select"
                  data-testid="product-color-selector"
                />
              </Form.Group>
            </div>
          )}

          {sizes.length > 0 && (
            <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label>Tamaño</Form.Label>
                <FavoriteDropdown
                  value={selectedSizeId}
                  onChange={(e) => handleSizeChange(e.target.value)}
                  options={sizes}
                  placeholder="Seleccionar tamaño"
                  nameField="size"
                  disabled={disabled || loading || !selectedModelId}
                  className="styled-select"
                  data-testid="product-size-selector"
                />
              </Form.Group>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ProductSelector;